import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api, productImageUrl, productDocumentUrl } from '@/lib/api';
import { PublicLayout } from '@/components/PublicLayout';
import { ArrowLeft, Download, FileText, Lock, Package, ShieldCheck } from 'lucide-react';
import { Kicker } from '@/components/Kicker';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

function StatCell({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-[#E4E9F0] py-3 last:border-b-0">
      <span className="text-sm text-[#4A4D52]">{label}</span>
      <span className="text-sm font-medium text-[#14171C]">{value ?? '—'}</span>
    </div>
  );
}

export default function ProductDetailPage({ portal = false }) {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['product', id, !!user],
    queryFn: async () => (await api.get(`/products/${id}`)).data,
    retry: false,
  });

  if (isLoading) return <PublicLayout><div className="pn-container py-20 text-sm text-muted-foreground">{t('common.loading')}</div></PublicLayout>;
  if (error) {
    const code = error?.response?.status;
    return (
      <PublicLayout>
        <div className="pn-container py-20">
          <div className="pn-card p-10 text-center">
            {code === 401 || code === 403 ? (
              <>
                <Lock className="mx-auto h-8 w-8 text-[#E2611C]" />
                <div className="mt-4 font-display text-xl font-semibold">{t('product.documents_locked')}</div>
                <Link to="/login" className="pn-btn-dark mt-6">{t('nav.login')}</Link>
              </>
            ) : (
              <div>{t('common.retry')}</div>
            )}
          </div>
        </div>
      </PublicLayout>
    );
  }

  const p = data.product;
  const docs = data.documents || [];
  const lockedCount = data.documents_locked_count || 0;

  const downloadDoc = async (attId, name) => {
    try {
      const token = localStorage.getItem('pn_token');
      const res = await fetch(productDocumentUrl(p.id, attId), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error(t('product.download_error'));
    }
  };

  return (
    <PublicLayout>
      <section className="pn-container py-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-[#4A4D52] hover:text-[#14171C]" data-testid="product-back">
          <ArrowLeft className="h-4 w-4" /> {t('product.back_catalog')}
        </button>

        <div className="mt-6 grid gap-10 lg:grid-cols-[1.1fr_.9fr]">
          <div>
            <div className="pn-card overflow-hidden">
              <div className="aspect-square w-full bg-[#F6F8FB] flex items-center justify-center">
                <img
                  src={productImageUrl(p.id)}
                  alt={p.name}
                  className="h-full w-full object-contain p-6"
                  onError={(e) => { e.currentTarget.style.opacity = 0.15; }}
                />
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="pn-card p-6">
                <Kicker>{t('product.category')}</Kicker>
                <div className="mt-2 font-display text-lg">{p.category_path || p.category_name || t('common.na')}</div>
              </div>
              <div className="pn-card p-6">
                <Kicker>{t('product.reference')}</Kicker>
                <div className="mt-2 font-mono text-lg text-[#14171C]">{p.default_code || t('common.na')}</div>
              </div>
            </div>
          </div>

          <div>
            <Kicker>{p.is_favorite ? t('product.favorite') : t('product.customer_specific')}</Kicker>
            <h1 className="mt-3 font-display text-3xl font-semibold text-[#14171C] sm:text-4xl">{p.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E9F0] bg-white px-3 py-1 text-xs font-medium text-[#14171C]">
                <Package className="h-3.5 w-3.5" />
                {t(`product.availability.${p.availability || 'unknown'}`)}
              </span>
              {p.offer_price ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#1F7A4D]/40 bg-[#E7F6EE] px-3 py-1 text-xs font-semibold text-[#1F7A4D]">
                  {t('offers.your_price')}: <span className="font-mono">{p.offer_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/{p.offer_unit || 'kg'}</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E2611C]/40 bg-white px-3 py-1 text-xs font-medium text-[#E2611C]">
                  {t('product.price_on_request')}
                </span>
              )}
            </div>
            {p.offer_notes && (
              <div className="mt-3 rounded-lg border border-[#E7F6EE] bg-[#F1FAF5] p-3 text-xs text-[#1F7A4D]">
                {p.offer_notes}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to={`/request/${p.id}?type=offer`} data-testid="product-detail-request-offer-button" className="pn-btn-dark">
                {t('product.request_offer')}
              </Link>
              <Link to={`/request/${p.id}?type=sample`} data-testid="product-detail-request-sample-button" className="pn-btn-outline">
                {t('product.request_sample')}
              </Link>
            </div>

            <div className="mt-8 pn-card p-6">
              <div className="font-display text-lg font-semibold text-[#14171C]">{t('product.details')}</div>
              <div className="mt-2">
                <StatCell label={t('product.material_type')} value={p.material_type} />
                <StatCell label={t('product.category')} value={p.category_path || p.category_name} />
                <StatCell label={t('product.reference_full')} value={p.default_code ? <span className="font-mono">{p.default_code}</span> : null} />
                <StatCell
                  label={t('product.weight')}
                  value={typeof p.qty_available === 'number' ? <span className="font-mono">{p.qty_available.toLocaleString(undefined, { maximumFractionDigits: 2 })} kg</span> : null}
                />
              </div>
            </div>

            {(p.tech_specs?.shore_a || p.tech_specs?.shore_d || p.tech_specs?.mfi_216 || p.tech_specs?.mfi_2160 || p.tech_specs?.verarbeitungsart || p.tech_specs?.base) && (
              <div className="mt-6 pn-card p-6">
                <div className="font-display text-lg font-semibold text-[#14171C]">{t('product.tech_specs')}</div>
                <div className="mt-2">
                  {p.tech_specs.verarbeitungsart ? <StatCell label={t('product.verarbeitungsart')} value={p.tech_specs.verarbeitungsart} /> : null}
                  {p.tech_specs.base ? <StatCell label={t('product.base')} value={p.tech_specs.base} /> : null}
                  {p.tech_specs.shore_a ? <StatCell label={t('product.shore_a')} value={<span className="font-mono">{p.tech_specs.shore_a}</span>} /> : null}
                  {p.tech_specs.shore_d ? <StatCell label={t('product.shore_d')} value={<span className="font-mono">{p.tech_specs.shore_d}</span>} /> : null}
                  {p.tech_specs.mfi_216 ? <StatCell label={t('product.mfi_216')} value={<span className="font-mono">{p.tech_specs.mfi_216} g/10min</span>} /> : null}
                  {p.tech_specs.mfi_2160 ? <StatCell label={t('product.mfi_2160')} value={<span className="font-mono">{p.tech_specs.mfi_2160} g/10min</span>} /> : null}
                </div>
              </div>
            )}

            <div className="mt-6 pn-card p-6">
              <div className="flex items-center justify-between">
                <div className="font-display text-lg font-semibold text-[#14171C] flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#E2611C]" /> {t('product.documents')}
                </div>
                <div className="flex items-center gap-1 text-xs text-[#4A4D52]"><ShieldCheck className="h-4 w-4" /> {user ? t('product.access_active') : t('product.protected')}</div>
              </div>
              <div className="mt-4 space-y-2">
                {user && docs.length === 0 && <div className="text-sm text-[#4A4D52]">{t('product.no_documents')}</div>}
                {!user && lockedCount > 0 && (
                  <div className="rounded-xl border border-dashed border-[#E4E9F0] bg-[#F6F8FB] p-4 text-sm">
                    <div className="flex items-center gap-2 text-[#14171C]"><Lock className="h-4 w-4" /> {lockedCount} {t('product.documents')}</div>
                    <div className="mt-1 text-[#4A4D52]">{t('product.documents_locked')}</div>
                    <Link to="/login" className="pn-btn-dark mt-3 !py-2 !px-4" data-testid="document-card-signin-button">{t('nav.login')}</Link>
                  </div>
                )}
                {docs.map((d) => {
                  const typeMap = {
                    wpz: { cls: 'bg-[#FBEADD] text-[#E2611C]', label: t('product.doc_types.wpz'), short: 'WPZ' },
                    coa: { cls: 'bg-[#E7F6EE] text-[#1F7A4D]', label: t('product.doc_types.coa'), short: 'COA' },
                    tds: { cls: 'bg-[#EEF2FF] text-[#3730A3]', label: t('product.doc_types.tds'), short: 'TDS' },
                    sds: { cls: 'bg-[#FEF2F2] text-[#B42318]', label: t('product.doc_types.sds'), short: 'SDS' },
                    other: { cls: 'bg-[#F6F8FB] text-[#4A4D52]', label: t('product.doc_types.other'), short: 'DOC' },
                  };
                  const meta = typeMap[d.doc_type] || typeMap.other;
                  return (
                    <div key={d.id} className="flex items-center justify-between rounded-xl border border-[#E4E9F0] bg-white p-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${d.is_wpz ? 'bg-[#FBEADD] text-[#E2611C]' : 'bg-[#F6F8FB] text-[#4A4D52]'}`}>
                          <FileText className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-[#14171C]">{d.name}</div>
                          <div className="text-xs text-[#4A4D52] flex items-center gap-2">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${meta.cls}`}>{meta.short}</span>
                            <span className="hidden sm:inline">{meta.label}</span>
                            <span>·</span>
                            <span>{d.mimetype}{d.file_size ? ` · ${Math.round(d.file_size / 1024)} KB` : ''}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        data-testid="document-card-download-button"
                        onClick={() => downloadDoc(d.id, d.name)}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#14171C] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#2b3038]"
                      >
                        <Download className="h-3.5 w-3.5" /> {t('common.download')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
